/**
 * Interactive Exam Modal — full-screen auto-marked assessment (topic tests & practice exams).
 *
 * Flow:
 *   1. Cover view — title, marks, sections, attempts used/remaining, best score
 *   2. Start → timer starts → student navigates sections via tabs
 *   3. Submit → auto-marks → Results view with section breakdown
 *   Lockdown exams: leaving the exam screen (tab switch / window blur) is
 *   counted as a violation; 3 violations auto-submit the attempt.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle, XCircle, Clock, Trophy, Loader2, Play,
  ChevronLeft, ChevronRight, AlertTriangle, RotateCcw, Sparkles, Lock,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_COLORS = [
  "from-sky-500 to-sky-700",
  "from-emerald-500 to-emerald-700",
  "from-amber-500 to-amber-700",
  "from-purple-500 to-purple-700",
  "from-rose-500 to-rose-700",
];

function sectionType(sec) {
  if (sec.type) return sec.type;
  if (sec.terms) return "matching";
  if (sec.questions?.[0]?.options) return "mcq";
  return "truefalse";
}

function formatTime(secs) {
  if (secs < 0) secs = 0;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

export default function InteractiveExamModal({ open, onClose, examId, subject = "ICT", yearLevel, token }) {
  const [view, setView] = useState("cover"); // 'cover' | 'exam' | 'results'
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [meta, setMeta] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentSection, setCurrentSection] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [result, setResult] = useState(null);
  const [violations, setViolations] = useState(0);
  const startTimeRef = useRef(null);
  const lastViolationRef = useRef(0);
  const viewRef = useRef(view);
  viewRef.current = view;

  const sectionKeys = useMemo(
    () => (meta ? Object.keys(meta.exam.sections).sort() : []),
    [meta]
  );

  const load = useCallback(async () => {
    if (!token || !open) return;
    setLoading(true);
    try {
      const url = examId
        ? `${API_URL}/api/interactive-exams/by-id/${examId}?token=${token}`
        : `${API_URL}/api/interactive-exams/${subject}/${yearLevel}?token=${token}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error("Exam not available");
      const data = await r.json();
      setMeta(data);
    } catch (e) {
      console.error("Load exam failed", e);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [examId, subject, yearLevel, token, open]);

  useEffect(() => {
    if (open) {
      setView("cover");
      setResult(null);
      setAnswers({});
      setCurrentSection(null);
      setSecondsLeft(null);
      setViolations(0);
      load();
    }
  }, [open, load]);

  // Timer
  useEffect(() => {
    if (view !== "exam" || secondsLeft === null) return;
    if (secondsLeft <= 0) {
      handleSubmit();
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, view]);

  // Lockdown: detect leaving the exam screen
  useEffect(() => {
    if (view !== "exam" || !meta?.lockdown) return;
    const registerViolation = () => {
      const now = Date.now();
      if (now - lastViolationRef.current < 1500) return;
      lastViolationRef.current = now;
      setViolations((v) => v + 1);
    };
    const onVisibility = () => { if (document.hidden) registerViolation(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", registerViolation);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", registerViolation);
    };
  }, [view, meta]);

  useEffect(() => {
    if (violations >= 3 && viewRef.current === "exam") handleSubmit();
  }, [violations]);

  const startExam = () => {
    if (!meta || meta.attempts_remaining <= 0) return;
    setAnswers(Object.fromEntries(sectionKeys.map((k) => [k, {}])));
    setSecondsLeft(meta.time_limit_minutes * 60);
    startTimeRef.current = new Date();
    setViolations(0);
    setView("exam");
    setCurrentSection(sectionKeys[0]);
    if (meta.lockdown && document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
  };

  const setAnswer = (sec, qnum, value) => {
    setAnswers((prev) => ({
      ...prev,
      [sec]: { ...prev[sec], [String(qnum)]: value },
    }));
  };

  const answeredCount = useMemo(() => {
    if (!meta) return { total: 0, answered: 0 };
    let total = 0;
    let answered = 0;
    for (const sec of sectionKeys) {
      const section = meta.exam.sections[sec];
      const items = section.questions || section.terms || [];
      total += items.length;
      for (const q of items) {
        if (answers[sec]?.[String(q.q_num)]) answered++;
      }
    }
    return { total, answered };
  }, [answers, meta, sectionKeys]);

  const handleSubmit = async () => {
    if (submitting || !meta || viewRef.current !== "exam") return;
    setSubmitting(true);
    const timeTaken = startTimeRef.current
      ? Math.floor((new Date() - startTimeRef.current) / 1000)
      : null;
    try {
      const r = await fetch(`${API_URL}/api/interactive-exams/${meta.id}/submit?token=${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          answers,
          time_taken_seconds: timeTaken,
          started_at: startTimeRef.current ? startTimeRef.current.toISOString() : null,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Submit failed");
      setResult(data);
      setView("results");
      exitFullscreen();
      await load(); // refresh attempts used
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const closeAll = () => {
    if (view === "exam" && !window.confirm("Exit the exam? Your answers will NOT be saved.")) return;
    exitFullscreen();
    onClose();
  };

  // ------ RENDER ------
  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeAll()}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden p-0 gap-0 flex flex-col" data-testid="interactive-exam-modal">
        <DialogHeader className="p-6 pb-4 bg-gradient-to-r from-[#0F2A4A] to-[#0E7C7B] text-white shrink-0">
          <DialogTitle className="text-2xl flex items-center gap-3 text-white">
            <Sparkles className="h-6 w-6 text-yellow-300" />
            {meta?.title || `Year ${yearLevel} ${subject} Interactive Exam`}
          </DialogTitle>
          {view === "exam" && (
            <div className="flex items-center justify-between text-sm mt-1">
              <span>Answered: {answeredCount.answered} / {answeredCount.total}</span>
              <span className={`font-mono px-3 py-1 rounded ${secondsLeft < 300 ? "bg-red-500/40 animate-pulse" : "bg-white/10"}`} data-testid="exam-timer">
                <Clock className="h-4 w-4 inline mr-2" />
                {formatTime(secondsLeft ?? 0)}
              </span>
            </div>
          )}
        </DialogHeader>

        {view === "exam" && meta?.lockdown && violations > 0 && (
          <div className="bg-red-600 text-white px-6 py-2 text-sm font-semibold flex items-center gap-2 shrink-0" data-testid="lockdown-warning">
            <AlertTriangle className="h-4 w-4" />
            Warning {violations}/3: You left the exam screen! After 3 warnings your exam is submitted automatically.
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex justify-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
            </div>
          )}

          {!loading && !meta && (
            <div className="text-center py-16 text-gray-600">No exam available for Year {yearLevel} {subject}.</div>
          )}

          {!loading && meta && view === "cover" && (
            <CoverView meta={meta} sectionKeys={sectionKeys} onStart={startExam} />
          )}

          {!loading && meta && view === "exam" && currentSection && (
            <ExamRunner
              exam={meta.exam}
              sectionKeys={sectionKeys}
              answers={answers}
              setAnswer={setAnswer}
              currentSection={currentSection}
              setCurrentSection={setCurrentSection}
            />
          )}

          {!loading && meta && view === "results" && result && (
            <ResultsView result={result} totalMarks={meta.total_marks} onClose={onClose} attemptsRemaining={result.attempts_remaining} />
          )}
        </div>

        {view === "exam" && (
          <div className="border-t bg-gray-50 p-4 flex items-center justify-between shrink-0">
            <div className="flex gap-2 flex-wrap">
              {sectionKeys.map((sec, i) => (
                <Button
                  key={sec}
                  variant={currentSection === sec ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentSection(sec)}
                  className={currentSection === sec ? `bg-gradient-to-r ${SECTION_COLORS[i % SECTION_COLORS.length]} text-white` : ""}
                  data-testid={`section-tab-${sec}`}
                >
                  Section {sec}
                </Button>
              ))}
            </div>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="submit-exam-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Submit ({answeredCount.answered}/{answeredCount.total})
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ============ Sub-views ============

function CoverView({ meta, sectionKeys, onStart }) {
  const attemptsLeft = meta.attempts_remaining;
  const canStart = attemptsLeft > 0;
  const kindLabel = meta.kind === "topic_test" ? "Topic Test" : meta.kind === "class_test" ? "Class Test" : "Practice Exam";

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold text-gray-800">{kindLabel}</h3>
        <p className="text-sm text-gray-500">Auto-marked · Timed · Best score kept{meta.lockdown ? " · Lockdown mode" : ""}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatCard label="Total Marks" value={meta.total_marks} icon="🎯" />
        <StatCard label="Time Limit" value={`${meta.time_limit_minutes} min`} icon="⏱️" />
        <StatCard label="Attempts Used" value={`${meta.attempts_used} / ${meta.max_attempts}`} icon="🎫" />
        <StatCard label="Best Score" value={meta.best_attempt ? `${meta.best_attempt.percentage}%` : "—"} icon="🏆" />
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 font-semibold text-sm text-gray-700">Sections</div>
        <div className="divide-y">
          {sectionKeys.map((key, i) => {
            const sec = meta.exam.sections[key];
            return (
              <div key={key} className="flex justify-between items-center px-4 py-2 text-sm">
                <span><span className={`inline-block w-6 h-6 rounded-full bg-gradient-to-br ${SECTION_COLORS[i % SECTION_COLORS.length]} text-white text-xs font-bold text-center leading-6 mr-2`}>{key}</span>{sec.name}</span>
                <Badge variant="secondary">{sec.marks} marks</Badge>
              </div>
            );
          })}
        </div>
      </div>

      {meta.my_attempts.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 font-semibold text-sm text-gray-700">Previous Attempts</div>
          <div className="divide-y">
            {meta.my_attempts.map((a) => (
              <div key={a.attempt_number} className="flex justify-between items-center px-4 py-2 text-sm">
                <span>Attempt #{a.attempt_number} · {new Date(a.submitted_at).toLocaleDateString()}</span>
                <Badge className={a.percentage >= 50 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>
                  {a.score}/{a.total_marks} · {a.percentage}%
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900 flex gap-3">
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold mb-1">Before you start</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>The timer starts when you click Start and auto-submits when it expires.</li>
            <li>You have a maximum of {meta.max_attempts} attempts — your best score is kept.</li>
            {meta.lockdown ? (
              <li className="font-semibold flex items-start gap-1"><Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Lockdown mode: leaving this screen (switching tabs or windows) gives a warning. 3 warnings = automatic submission.</li>
            ) : (
              <li>Do not refresh the page or close this window during the exam.</li>
            )}
          </ul>
        </div>
      </div>

      {canStart ? (
        <Button
          onClick={onStart}
          size="lg"
          className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg"
          data-testid="start-exam-btn"
        >
          <Play className="h-5 w-5 mr-2 fill-current" />
          Start ({attemptsLeft} attempt{attemptsLeft === 1 ? "" : "s"} remaining)
        </Button>
      ) : (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center text-red-800 font-medium" data-testid="no-attempts-left">
          You have used all {meta.max_attempts} attempts. Contact your teacher if you need another one.
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="border rounded-lg p-3 flex items-center gap-3 bg-white">
      <div className="text-2xl">{icon}</div>
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="font-semibold text-gray-800">{value}</div>
      </div>
    </div>
  );
}

function ExamRunner({ exam, sectionKeys, answers, setAnswer, currentSection, setCurrentSection }) {
  const section = exam.sections[currentSection];
  const idx = sectionKeys.indexOf(currentSection);
  const type = sectionType(section);

  return (
    <div>
      <div className={`rounded-lg p-4 mb-4 bg-gradient-to-r ${SECTION_COLORS[idx % SECTION_COLORS.length]} text-white`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Section {currentSection}: {section.name}</h3>
            <p className="text-white/80 text-sm">{section.marks} marks</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" disabled={idx === 0} onClick={() => setCurrentSection(sectionKeys[idx - 1])} data-testid="section-prev">
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button size="sm" variant="secondary" disabled={idx === sectionKeys.length - 1} onClick={() => setCurrentSection(sectionKeys[idx + 1])} data-testid="section-next">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {type === "mcq" ? (
        <MCQList
          section={currentSection}
          questions={section.questions}
          answers={answers[currentSection] || {}}
          setAnswer={setAnswer}
        />
      ) : type === "truefalse" ? (
        <TrueFalseList
          section={currentSection}
          questions={section.questions}
          answers={answers[currentSection] || {}}
          setAnswer={setAnswer}
        />
      ) : (
        <MatchingList
          section={currentSection}
          terms={section.terms}
          definitions={section.definitions}
          answers={answers[currentSection] || {}}
          setAnswer={setAnswer}
        />
      )}
    </div>
  );
}

function MCQList({ section, questions, answers, setAnswer }) {
  return (
    <div className="space-y-4">
      {questions.map((q) => (
        <div key={q.q_num} className="border rounded-lg p-4 bg-white" data-testid={`q-${section}-${q.q_num}`}>
          <div className="flex gap-3 mb-3">
            <span className="font-bold text-emerald-700 shrink-0">Q{q.q_num}.</span>
            <div className="flex-1">
              {(q.chapter || q.ref) && <div className="text-xs text-gray-500 uppercase mb-1">{q.chapter || q.ref}</div>}
              <p className="text-gray-800 whitespace-pre-line">{q.question}</p>
            </div>
            <Badge variant="secondary" className="shrink-0 h-fit">{q.marks} mk</Badge>
          </div>
          <div className="space-y-2 ml-8">
            {Object.entries(q.options).map(([letter, txt]) => (
              <label
                key={letter}
                className={`flex items-start gap-2 p-2 rounded cursor-pointer border transition-colors ${
                  answers[String(q.q_num)] === letter
                    ? "bg-emerald-50 border-emerald-400"
                    : "hover:bg-gray-50 border-transparent"
                }`}
              >
                <input
                  type="radio"
                  name={`${section}-${q.q_num}`}
                  value={letter}
                  checked={answers[String(q.q_num)] === letter}
                  onChange={() => setAnswer(section, q.q_num, letter)}
                  className="mt-1"
                  data-testid={`q-${section}-${q.q_num}-opt-${letter}`}
                />
                <span className="font-semibold">{letter})</span>
                <span className="flex-1 text-sm">{txt}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TrueFalseList({ section, questions, answers, setAnswer }) {
  return (
    <div className="space-y-3">
      {questions.map((q) => (
        <div key={q.q_num} className="border rounded-lg p-4 bg-white" data-testid={`q-${section}-${q.q_num}`}>
          <div className="flex gap-3 mb-3">
            <span className="font-bold text-emerald-700 shrink-0">Q{q.q_num}.</span>
            <p className="flex-1 text-gray-800">{q.question}</p>
          </div>
          <div className="flex gap-3 ml-8">
            {["TRUE", "FALSE"].map((v) => (
              <button
                key={v}
                onClick={() => setAnswer(section, q.q_num, v)}
                className={`flex-1 py-2 rounded border font-semibold transition-colors ${
                  answers[String(q.q_num)] === v
                    ? v === "TRUE"
                      ? "bg-emerald-500 text-white border-emerald-600"
                      : "bg-red-500 text-white border-red-600"
                    : "bg-white hover:bg-gray-50"
                }`}
                data-testid={`q-${section}-${q.q_num}-${v}`}
              >
                {v === "TRUE" ? <CheckCircle className="h-4 w-4 inline mr-1" /> : <XCircle className="h-4 w-4 inline mr-1" />}
                {v}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MatchingList({ section, terms, definitions, answers, setAnswer }) {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h4 className="font-semibold text-gray-700 mb-3">Terms</h4>
        <div className="space-y-2">
          {terms.map((t) => (
            <div key={t.q_num} className="flex items-center gap-2 border rounded p-2 bg-white" data-testid={`q-${section}-${t.q_num}`}>
              <span className="font-bold text-emerald-700 w-6">{t.q_num}.</span>
              <span className="flex-1 font-medium text-gray-800">{t.term}</span>
              <select
                value={answers[String(t.q_num)] || ""}
                onChange={(e) => setAnswer(section, t.q_num, e.target.value)}
                className="border rounded px-2 py-1 w-20 text-center font-semibold"
                data-testid={`q-${section}-${t.q_num}-select`}
              >
                <option value="">—</option>
                {definitions.map((d) => (
                  <option key={d.letter} value={d.letter}>{d.letter}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-gray-700 mb-3">Definitions (Reference)</h4>
        <div className="space-y-1 text-sm border rounded p-3 bg-gray-50 max-h-[500px] overflow-y-auto">
          {definitions.map((d) => (
            <div key={d.letter} className="flex gap-2 py-1">
              <span className="font-bold w-6 shrink-0">{d.letter}.</span>
              <span className="flex-1 text-gray-700">{d.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ResultsView({ result, totalMarks, onClose, attemptsRemaining }) {
  const pct = result.percentage;
  const grade = pct >= 80 ? "A" : pct >= 70 ? "B" : pct >= 60 ? "C" : pct >= 50 ? "D" : "E";
  const gradeColor = pct >= 60 ? "text-emerald-600" : pct >= 50 ? "text-amber-600" : "text-red-600";

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="exam-results">
      <div className="text-center space-y-2">
        <Trophy className={`h-16 w-16 mx-auto ${gradeColor}`} />
        <h2 className="text-3xl font-bold">Submitted!</h2>
        <div className={`text-6xl font-bold ${gradeColor}`}>{result.score}<span className="text-2xl text-gray-400">/{totalMarks}</span></div>
        <div className="text-lg text-gray-600">Grade: <span className={`font-bold ${gradeColor}`}>{grade}</span> · {pct}%</div>
      </div>

      <Progress value={pct} className="h-3" />

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 font-semibold text-sm text-gray-700">Section Breakdown</div>
        <div className="divide-y">
          {Object.entries(result.section_scores).map(([key, sc]) => {
            const p = sc.available > 0 ? Math.round((sc.earned / sc.available) * 100) : 0;
            return (
              <div key={key} className="px-4 py-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">Section {key} · {sc.name}</span>
                  <span className="font-mono">{sc.earned} / {sc.available} ({p}%)</span>
                </div>
                <Progress value={p} className="h-2" />
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center text-sm text-blue-900">
        {attemptsRemaining > 0 ? (
          <><RotateCcw className="h-4 w-4 inline mr-1" /> You have {attemptsRemaining} attempt{attemptsRemaining === 1 ? "" : "s"} remaining. Your best score will be kept.</>
        ) : (
          <>You&apos;ve used all your attempts. Well done!</>
        )}
      </div>

      <Button onClick={onClose} className="w-full" data-testid="close-results-btn">Close</Button>
    </div>
  );
}
